import { useEffect, useMemo, useState } from "react";
import {
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  DropAnimation,
  defaultDropAnimation,
  pointerWithin,
  useDroppable,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import BoardSection from "./BoardSection";
import TaskItem from "./TaskItem";
import { ApiService } from "api/api.service";
import { BoardResource, CardDto } from "dtos/kanban.dto";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  message,
  notification,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import * as TbIcons from "react-icons/tb";
import { useParams, useSearchParams } from "react-router-dom";
import { T_PageTitle } from "store/pageTitleSlice";
import { EditColumnsModal } from "./EditColumns";
import { resourceConfig } from "./BoardCard";
import { IconType } from "react-icons";
import { AssignCardsModal } from "./AssignCards";
import { GROUP_COLOR_PALETTE } from "./MetaItems";
import PipelineActivity from "components/Pipeline/PipelineActivity";

const UNGROUPED_KEY = "__ungrouped__";
export type FilterType = "createdAt" | "columns";
export type GroupColor = {
  solid: string;
  soft: string;
  border: string;
};

export const DEFAULT_COLOR: GroupColor = {
  solid: "#6b7280",
  soft: "#f3f4f6",
  border: "#e5e7eb",
};

const CollapsedColumnPreview = ({
  columnId,
  columnIcon,
  columnName,
  columnDescription,
  cardCount,
  borderColor,
  solidColor,
  softColor,
  disabled,
}: {
  columnId: string;
  columnIcon: string;
  columnName: string;
  columnDescription: string;
  cardCount: number;
  borderColor: string;
  solidColor: string;
  softColor: string;
  disabled?: boolean;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    disabled,
  });

  const Icon = (columnIcon && (TbIcons as Record<string, IconType>)[columnIcon]) || null;

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-lg px-3 py-2 border flex items-center justify-between gap-2 transition-all ${
        isOver && !disabled ? "ring-2 ring-violet-500 scale-[1.02]" : ""
      } ${disabled ? "opacity-50" : ""}`}
      style={{ borderColor: isOver && !disabled ? "#8b5cf6" : borderColor }}
    >
      <Space>
        {Icon && (
          <div
            className="size-8 flex items-center justify-center rounded-md"
            style={{ backgroundColor: softColor, color: solidColor }}
          >
            <Icon size={16} />
          </div>
        )}
        <Space.Compact direction="vertical">
          <span className="text-xs font-medium truncate" title={columnName}>
            {columnName}
          </span>
          <span
            className={`text-[10px] font-medium truncate ${columnDescription?.length ? "text-gray-400" : "italic text-gray-400/80"}`}
            title={columnDescription || "No Description"}
          >
            {columnDescription || "No Description"}
          </span>
        </Space.Compact>
      </Space>
      <Tag bordered={false} className="!m-0 !text-xs !px-1.5 !py-0 !bg-gray-100 flex-shrink-0">
        {cardCount}
      </Tag>
    </div>
  );
};

interface SyncCardsButtonProps {
  boardId: string | undefined;
  isSyncing: boolean;
  syncAgainIn: number;
  onSync: (params: { boardId: string; mode?: "async" | "sync" }) => void;
}

function SyncCardsButton({ boardId, isSyncing, syncAgainIn, onSync }: Readonly<SyncCardsButtonProps>) {
  const [devMenuOpen, setDevMenuOpen] = useState(false);

  const triggerSync = (mode: "async" | "sync" = "async") => {
    if (!boardId) return message.error("Board Id not found");
    onSync({ boardId, mode });
  };

  useEffect(() => {
    if (!devMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        setDevMenuOpen(false);
        triggerSync("sync");
      }
      if (e.key === "Escape") setDevMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [devMenuOpen, boardId]);

  const disabled = syncAgainIn > 0;

  return (
    <Tooltip title={syncAgainIn ? `Sync again in ${Math.ceil(syncAgainIn / 1000)} seconds` : undefined}>
      <Button
        variant="solid"
        color="volcano"
        loading={isSyncing}
        disabled={disabled}
        onContextMenu={(e) => {
          e.preventDefault();
          setDevMenuOpen(true);
        }}
        onClick={() => triggerSync()}
      >
        {isSyncing ? "Syncing" : "Sync"} Cards
      </Button>
    </Tooltip>
  );
}

const BoardSectionList = () => {
  const { id: boardId } = useParams();
  const [sp] = useSearchParams();
  const scopeId = sp.get("scopeId") ?? undefined;

  const boardQuery = ApiService.kanban.useGetBoardData(boardId);
  const [assignCards, setAssignCards] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showColumns, setShowColumns] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<{
    filterType: FilterType;
    columnIds: string[];
    startDate?: string;
    endDate?: string;
    search?: string;
    scope?: Record<string, string>;
  }>({
    filterType: "createdAt",
    columnIds: [],
    scope: {},
  });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getCardsColumnWise = ApiService.kanban.useGetCard({
    boardId,
    type: appliedFilters.filterType,
    column_id: appliedFilters.columnIds ?? [],
    start_date: appliedFilters.startDate,
    end_date: appliedFilters.endDate,
    search: appliedFilters.search,
    scope: appliedFilters.scope,
    checkReady() {
      if (!boardQuery.data?.result._id?.length) return false;
      const scope = boardQuery.data?.result.scope;
      if (!scope) return true;
      return Boolean(appliedFilters.scope?.[scope]);
    },
  });
  const unassignedCards = getCardsColumnWise.data?.result.find((res) => !res.columnId)?.cards ?? [];

  const moveCardMutation = ApiService.kanban.useMoveCard(boardId);
  const moveCardsMutation = ApiService.kanban.useMoveCards(boardId);
  const undoCardMutation = ApiService.kanban.useUndoCard();
  const syncCards = ApiService.kanban.useSyncCards();

  const scopedItems = ApiService.kanban.useGetScopeByItemsInfinite(
    boardQuery.data?.result,
    { defaultLimit: 30, scopeId },
    () => {
      const scope = boardQuery.data?.result.scope;
      return Boolean(scope?.length);
    },
  );

  const orgCode = useMemo(() => {
    const org = boardQuery.data?.result.organisation;
    return typeof org === "object" && org !== null ? org.code : undefined;
  }, [boardQuery.data?.result.organisation]);

  const scopedItemOptions = useMemo(() => {
    return scopedItems.query.data?.pages.flatMap((page) => page.data) ?? [];
  }, [scopedItems.query.data]);

  const columns = useMemo(() => {
    return boardQuery.data?.result?.columns || [];
  }, [boardQuery.data]);

  const extractedColumns = useMemo(() => {
    return (
      boardQuery.data?.result?.columns?.map((col) => ({
        id: col.id,
        name: col.name,
        group: col.group,
      })) ?? []
    );
  }, [boardQuery.data]);

  const initialBoardSections = useMemo(() => {
    const sections: Record<string, CardDto[]> = {};

    columns.forEach((column) => {
      sections[column.id] = [];
    });

    if (getCardsColumnWise.data?.result) {
      getCardsColumnWise.data.result.forEach((columnData: any) => {
        sections[columnData.columnId] = columnData.cards;
      });
    }

    return sections;
  }, [getCardsColumnWise.data, columns]);

  const [boardSections, setBoardSections] = useState<Record<string, CardDto[]>>({});

  useEffect(() => {
    const scope = boardQuery.data?.result.scope;
    if (!scope || scopeId || !scopedItemOptions?.length) return;

    setAppliedFilters((prev) => {
      const firstOption = scopedItemOptions[0].value;
      if (prev.scope?.[scope] || !firstOption) {
        return prev;
      }

      return {
        ...prev,
        scope: {
          [scope]: firstOption,
        },
      };
    });
  }, [scopedItemOptions, boardQuery.data?.result.scope, scopeId]);

  const groupedItems = useMemo(() => {
    return Object.values(
      extractedColumns.reduce(
        (acc, el) => {
          const group = el.group ?? "Ungrouped";

          if (!acc[group]) {
            acc[group] = {
              type: "group",
              label: group,
              children: [],
            };
          }

          acc[group].children.push({
            key: String(el.id),
            label: (
              <div className="flex items-center gap-2">
                <Checkbox checked={!!showColumns[el.id]} />
                <span>{el.name}</span>
              </div>
            ),
          });

          return acc;
        },
        {} as Record<string, any>,
      ),
    );
  }, [extractedColumns, showColumns]);

  useEffect(() => {
    if (Object.keys(initialBoardSections).length > 0) {
      setBoardSections(initialBoardSections);
    }
  }, [initialBoardSections]);

  useEffect(() => {
    const scope = boardQuery.data?.result.scope;
    if (scope && scopeId) {
      setAppliedFilters((prev) => ({
        ...prev,
        scope: { [scope]: scopeId },
      }));
    }
  }, [scopeId, boardQuery.data?.result.scope]);

  const [activeCardId, setActiveCardId] = useState<null | string>(null);

  const groups = useMemo(() => {
    return boardQuery.data?.result?.groups ?? [];
  }, [boardQuery.data]);

  const groupColorMap = useMemo(() => {
    const map: Record<string, (typeof GROUP_COLOR_PALETTE)[number]> = {};
    for (const g of groups) {
      const colorKey = ((g.meta?.color as string) ?? "").replace("#", "").toLocaleLowerCase();
      const color = GROUP_COLOR_PALETTE.find((c) =>
        [c.solid, c.soft, c.border].some((hex) => hex.replace("#", "").toLocaleLowerCase() === colorKey),
      );

      if (color) map[g.name] = color;
    }

    map[UNGROUPED_KEY] = GROUP_COLOR_PALETTE[GROUP_COLOR_PALETTE.length - 1];
    return map;
  }, [groups]);
  const visibleGroupedColumns = useMemo(() => {
    const visible = columns.filter((col) => showColumns[col.id]).sort((a, b) => a.position - b.position);

    const buckets = new Map<string, typeof visible>();

    visible.forEach((col) => {
      const key = col.group ?? UNGROUPED_KEY;

      if (!buckets.has(key)) {
        buckets.set(key, []);
      }

      buckets.get(key)!.push(col);
    });

    const orderedGroups = [...groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const result: { name: string; columns: typeof visible }[] = [];

    // ALWAYS SHOW UNGROUPED FIRST
    const ungrouped = buckets.get(UNGROUPED_KEY);

    if (ungrouped?.length) {
      result.push({
        name: "Ungrouped",
        columns: ungrouped,
      });

      buckets.delete(UNGROUPED_KEY);
    }

    // THEN NORMAL GROUPS
    orderedGroups.forEach((g) => {
      const cols = buckets.get(g.name);

      if (cols?.length) {
        result.push({
          name: g.name,
          columns: cols,
        });
      }

      buckets.delete(g.name);
    });

    // ANY UNKNOWN GROUPS LAST
    buckets.forEach((cols, name) => {
      if (cols.length) {
        result.push({
          name,
          columns: cols,
        });
      }
    });

    return result;
  }, [columns, showColumns, groups]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const findCardContainer = (cardId: string): string | undefined => {
    const allColumnIds = columns.map((col) => col.id);
    if (allColumnIds.includes(cardId)) {
      return cardId;
    }

    for (const [columnId, cards] of Object.entries(boardSections)) {
      if (cards.some((card) => card._id === cardId)) {
        return columnId;
      }
    }

    return undefined;
  };

  const getCardById = (cardId: string): CardDto | null => {
    for (const cards of Object.values(boardSections)) {
      const card = cards.find((c) => c._id === cardId);
      if (card) return card;
    }
    return null;
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveCardId(active.id as string);
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) return;

    const activeContainer = findCardContainer(active.id as string);
    const overContainer = findCardContainer(over.id as string);

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    // Check if the target column is disabled
    const targetColumn = columns.find((col) => col.id === overContainer);
    if (targetColumn?.disabled) {
      return;
    }

    setBoardSections((boardSection) => {
      const activeItems = boardSection[activeContainer];
      const overItems = boardSection[overContainer];

      const activeIndex = activeItems.findIndex((item) => item._id === active.id);
      const overIndex = overItems.findIndex((item) => item._id === over.id);

      const activeCard = activeItems[activeIndex];

      return {
        ...boardSection,
        [activeContainer]: activeItems.filter((item) => item._id !== active.id),
        [overContainer]: [
          ...overItems.slice(0, overIndex >= 0 ? overIndex : overItems.length),
          activeCard,
          ...overItems.slice(overIndex >= 0 ? overIndex : overItems.length),
        ],
      };
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) {
      setActiveCardId(null);
      return;
    }

    const activeContainer = findCardContainer(active.id as string);
    const overContainer = findCardContainer(over.id as string);

    if (!activeContainer || !overContainer) {
      setActiveCardId(null);
      return;
    }

    // Check if the target column is disabled
    const targetColumn = columns.find((col) => col.id === overContainer);
    if (targetColumn?.disabled) {
      message.warning(`Cannot drop card in disabled column: ${targetColumn.name}`);
      setActiveCardId(null);
      return;
    }

    const activeCard = getCardById(active.id as string);

    if (!activeCard) {
      message.error("Card not found");
      setActiveCardId(null);
      return;
    }

    const originalSections = { ...boardSections };

    moveCardMutation.mutate(
      {
        cardId: active.id as string,
        to_column_id: overContainer,
      },
      {
        onSuccess: (res) => {
          const cardID = res.result._id;
          notification.info({
            message: "Card moved successfully",
            description: "The card has been moved to its new stage. You can undo this action if it was unintentional.",
            placement: "bottomRight",

            actions: (
              <Button
                onClick={() => {
                  undoCardMutation.mutate({ card_id: cardID });
                }}
                size="small"
                type="link"
              >
                Undo
              </Button>
            ),
          });
        },
        onError: (error) => {
          console.error("Move card error:", error);
          setBoardSections(originalSections);
          message.error(`Failed to move card: ${error.message || "Unknown error"}`);
        },
      },
    );

    if (activeContainer === overContainer) {
      const activeIndex = boardSections[activeContainer].findIndex((card) => card._id === active.id);
      const overIndex = boardSections[overContainer].findIndex((card) => card._id === over.id);

      if (activeIndex !== overIndex) {
        setBoardSections((boardSection) => ({
          ...boardSection,
          [overContainer]: arrayMove(boardSection[overContainer], activeIndex, overIndex),
        }));
      }
    }

    setActiveCardId(null);
  };

  const dropAnimation: DropAnimation = {
    ...defaultDropAnimation,
  };

  const defaultColumnsActiveId = new Set(columns.map((item) => item.id));
  const activeCard = activeCardId ? getCardById(activeCardId) : null;
  const showScopeSelector = !scopeId?.length && boardQuery.data?.result.scope?.length;

  const selectedScopeValue = useMemo(() => {
    if (!showScopeSelector || !boardQuery.data?.result.scope || !appliedFilters.scope) return undefined;
    return appliedFilters.scope[boardQuery.data?.result.scope];
  }, [appliedFilters.scope]);

  useEffect(() => {
    if (!columns.length) return;

    setShowColumns(() => {
      const initialState: Record<string, boolean> = {};

      columns.forEach((col) => {
        initialState[col.id] = defaultColumnsActiveId.has(col.id);
      });

      return initialState;
    });
  }, [columns]);

  const pageTitleData: T_PageTitle = [{ title: "Pipelines", link: "/dashboard/operations/pipelines" }];
  if (boardQuery.data?.result.name) {
    pageTitleData.push({
      title: boardQuery.data?.result.name,
      link: `/dashboard/operations/pipelines/${boardQuery.data.result._id}`,
    });
  }

  if (scopeId) {
    const option = scopedItemOptions.find((option) => option.value === scopeId);
    const title = option?.title || boardQuery.data?.result.resource;
    if (title) {
      pageTitleData.push({
        title,
      });
    }
  }

  const ScopeIcon = boardQuery.data?.result.scope
    ? resourceConfig[boardQuery.data.result.scope as BoardResource].Icon
    : () => null;

  if (boardQuery.isLoading) {
    return (
      <DashboardLayout>
        <PageTitle pageTitle={pageTitleData} title={"Pipeline"} />
        <div className="h-[80vh] flex items-center justify-center">
          <TbIcons.TbLoader2 size={48} className="stroke-violet-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const syncAgainIn = boardQuery.data?.result.isSyncing
    ? 0
    : boardQuery.data?.result.cooldownTill
      ? Math.max(0, new Date(boardQuery.data.result.cooldownTill).getTime() - now)
      : 0;

  return (
    <DashboardLayout
      headerRight={
        <Flex gap={8}>
          <SyncCardsButton
            boardId={boardId}
            isSyncing={Boolean(boardQuery.data?.result.isSyncing)}
            syncAgainIn={syncAgainIn}
            onSync={({ boardId, mode }) =>
              syncCards.mutate(
                { boardId, mode },
                { onSuccess: () => message.success("Cards are being synced to this board") },
              )
            }
          />
          <Button onClick={() => setAssignCards(true)} variant="solid" color="cyan">
            Assign {unassignedCards.length || ""} Cards
          </Button>
          <Button onClick={() => setIsEditing(true)} variant="solid" color="purple">
            Manage Columns
          </Button>
          <Button onClick={() => setIsDrawerOpen(true)}>View Activity</Button>
        </Flex>
      }
    >
      <PageTitle pageTitle={pageTitleData} title={boardQuery.data?.result.name || "Pipeline"} />
      <EditColumnsModal
        open={isEditing}
        onCancel={() => setIsEditing(false)}
        onOk={() => setIsEditing(false)}
        boardId={boardId}
        refetch={boardQuery.refetch}
      />
      <AssignCardsModal
        open={assignCards}
        onCancel={() => setAssignCards(false)}
        refetch={boardQuery.refetch}
        boardId={boardId}
        cards={unassignedCards}
        onAssign={(payload) => {
          moveCardsMutation.mutate(
            payload.map((value) => ({ card_id: value.cardId, column_id: value.columnId })),
            {
              onSuccess() {
                message.success(`Assigned ${payload.length} card${payload.length === 1 ? "" : "s"}`);
              },
            },
          );
        }}
      />
      {boardQuery.isPending ? (
        <div className="h-[75vh] flex items-center justify-center">
          <TbIcons.TbLoader2 className="stroke-violet-600 animate-spin" size={48} />
        </div>
      ) : (
        <div className="p-4 grow flex-1 h-full">
          <div className="mb-0 flex items-center justify-between gap-3">
            <Space>
              <Input
                type="text"
                prefix={<TbIcons.TbSearch />}
                placeholder={`Search for ${boardQuery.data?.result.resource || "Item"}s`}
                className="w-80"
                value={appliedFilters.search}
                onChange={(e) =>
                  setAppliedFilters((prev) => ({
                    ...prev,
                    search: e.target.value,
                  }))
                }
              />
            </Space>

            <Space>
              {showScopeSelector && (
                <Select
                  showSearch
                  filterOption={false}
                  className="w-64"
                  virtual
                  prefix={<ScopeIcon className="mr-1" size={16} />}
                  placeholder={`Select a ${boardQuery.data?.result.scope || "Scope"}`}
                  options={scopedItemOptions.map((item, index) => ({
                    label: (
                      <Space.Compact className="items-center">
                        <Tag>{index + 1}</Tag>
                        <Typography.Text>{item.title}</Typography.Text>
                      </Space.Compact>
                    ),
                    value: item.value,
                    key: `${index}.${item.title}`,
                  }))}
                  value={selectedScopeValue}
                  loading={scopedItems.query.isLoading}
                  onSearch={(value) => scopedItems.setSearch(value)}
                  onPopupScroll={(e) => {
                    const target = e.target as HTMLDivElement;

                    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20;

                    if (isNearBottom && scopedItems.query.hasNextPage && !scopedItems.query.isFetchingNextPage) {
                      scopedItems.query.fetchNextPage();
                    }
                  }}
                  onChange={(value) => {
                    const scope = boardQuery.data?.result.scope;

                    if (!scope) return;

                    setAppliedFilters((prev) => ({
                      ...prev,
                      scope: {
                        [scope]: value,
                      },
                    }));
                  }}
                />
              )}
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: groupedItems,
                  onClick: ({ key }) => {
                    setShowColumns((prev) => ({
                      ...prev,
                      [key]: !prev[key],
                    }));
                  },
                }}
              >
                <Button icon={<TbIcons.TbColumns3 />}>
                  Columns
                  <Tag bordered={false} className="!m-0 !p-0 !bg-gray-100 !font-medium">
                    {extractedColumns.filter((col) => showColumns[col.id]).length}
                  </Tag>
                </Button>
              </Dropdown>
            </Space>

            {/* <div className="flex gap-2">
              <RangePicker
                value={
                  appliedFilters.startDate && appliedFilters.endDate
                    ? [dayjs(appliedFilters.startDate), dayjs(appliedFilters.endDate)]
                    : null
                }
                onChange={(dates) => {
                  if (!dates) {
                    setAppliedFilters((prev) => ({
                      ...prev,
                      startDate: undefined,
                      endDate: undefined,
                    }));
                    return;
                  }

                  const [start, end] = dates;

                  if (start && end) {
                    setAppliedFilters((prev) => ({
                      ...prev,
                      startDate: start.format("YYYY-MM-DD"),
                      endDate: end.format("YYYY-MM-DD"),
                    }));
                  }
                }}
                className="w-[240px]"
              />

              <Select
                value={appliedFilters.filterType}
                className="w-[240px]"
                dropdownStyle={{ minWidth: 320 }}
                dropdownRender={() => (
                  <div className="p-3">
                    <div className="flex gap-4 pb-2">
                      <div
                        className={`flex items-center gap-2 cursor-pointer ${
                          appliedFilters.filterType === "createdAt" ? "font-semibold" : ""
                        }`}
                        onClick={() =>
                          setAppliedFilters((prev) => ({
                            ...prev,
                            filterType: "createdAt",
                            columnIds: [],
                          }))
                        }
                      >
                        <Checkbox checked={appliedFilters.filterType === "createdAt"} />
                        <span>Created At</span>
                      </div>

                      <div
                        className={`flex items-center gap-2 cursor-pointer ${
                          appliedFilters.filterType === "columns" ? "font-semibold" : ""
                        }`}
                        onClick={() =>
                          setAppliedFilters((prev) => ({
                            ...prev,
                            filterType: "columns",
                          }))
                        }
                      >
                        <Checkbox checked={appliedFilters.filterType === "columns"} />
                        <span>Columns</span>
                      </div>
                    </div>

                    {appliedFilters.filterType === "columns" && (
                      <div className="flex flex-col gap-2 pl-4 border-l border-gray-200">
                        {columns.map((col) => (
                          <label key={col.id} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={appliedFilters.columnIds.includes(col.id)}
                              onChange={(e) =>
                                setAppliedFilters((prev) => ({
                                  ...prev,
                                  columnIds: e.target.checked
                                    ? [...prev.columnIds, col.id]
                                    : prev.columnIds.filter((id) => id !== col.id),
                                }))
                              }
                            />
                            <span className="whitespace-normal">{col.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              >
                <Select.Option value="createdAt">Created At</Select.Option>
                <Select.Option value="columns">Columns</Select.Option>
              </Select>
            </div> */}
          </div>

          {columns.length === 0 ? (
            <div className="w-full h-[60vh] flex justify-center items-center">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No columns found" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <div className="flex pt-4 gap-6 items-start overflow-x-auto hide-scrollbar">
                {visibleGroupedColumns.map((group) => {
                  const color = groupColorMap[group.name] ?? DEFAULT_COLOR;
                  const totalCards = group.columns.reduce((sum, col) => sum + (boardSections[col.id]?.length ?? 0), 0);

                  return (
                    <div
                      key={group.name}
                      className="rounded-2xl min-w-80 border h-full min-h-[80vh] flex-shrink-0 flex flex-col"
                      style={{
                        backgroundColor: color.soft,
                        borderColor: color.border,
                      }}
                    >
                      <div
                        className="flex items-center justify-between px-4 py-2.5 rounded-t-2xl sticky top-0 z-10"
                        style={{
                          backgroundColor: color.solid,
                          color: "#fff",
                        }}
                      >
                        <div className="flex items-center gap-2 sticky" style={{ left: "1rem" }}>
                          <Space.Compact className="items-center">
                            <Button
                              type="text"
                              className="!p-0 !size-6 !border-none !shadow-none hover:!bg-transparent !text-white"
                              icon={
                                collapsedGroups.includes(group.name) ? (
                                  <TbIcons.TbChevronRightPipe size={16} />
                                ) : (
                                  <TbIcons.TbChevronLeftPipe size={16} />
                                )
                              }
                              onClick={() => {
                                setCollapsedGroups((prev) => {
                                  if (prev.includes(group.name)) {
                                    return prev.filter((grp) => grp !== group.name);
                                  } else {
                                    return [...prev, group.name];
                                  }
                                });
                              }}
                            />
                            <span className="font-semibold text-sm tracking-wide">{group.name}</span>
                          </Space.Compact>
                          {!collapsedGroups.includes(group.name) && (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full font-medium"
                              style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                            >
                              <Space className="gap-1">
                                <TbIcons.TbColumns3 className="inline" />
                                <span>
                                  {group.columns.length} {group.columns.length === 1 ? "column" : "columns"}
                                </span>
                              </Space>
                              <Space className="gap-1 ml-3">
                                <TbIcons.TbLayoutCards className="inline" />
                                <span>
                                  {totalCards} {totalCards === 1 ? "card" : "cards"}
                                </span>
                              </Space>
                            </span>
                          )}
                        </div>
                      </div>

                      {collapsedGroups.includes(group.name) ? (
                        <div className="flex flex-col gap-2 p-3 flex-1">
                          {group.columns.map((column) => (
                            <CollapsedColumnPreview
                              key={column.id}
                              columnId={column.id}
                              columnName={column.name}
                              columnDescription={column.meta?.description}
                              cardCount={boardSections[column.id]?.length ?? 0}
                              borderColor={color.border}
                              solidColor={color.solid}
                              softColor={color.soft}
                              disabled={column.disabled}
                              columnIcon={column.meta?.icon}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex gap-3 p-3 flex-1 grow">
                          {group.columns.map((column) => (
                            <div key={column.id}>
                              <BoardSection
                                disabled={column.disabled}
                                id={column.id}
                                isPending={getCardsColumnWise.isPending}
                                title={column.name}
                                description={column.meta?.description}
                                cards={boardSections[column.id] || []}
                                type={boardQuery.data?.result.resource ?? ""}
                                iconName={column.meta?.icon}
                                groupColor={color}
                                templates={column.templates}
                                orgCode={orgCode}
                                action={column.action}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <DragOverlay dropAnimation={dropAnimation}>
                  {activeCard ? <TaskItem itemData={activeCard} /> : null}
                </DragOverlay>
              </div>
            </DndContext>
          )}
        </div>
      )}
      <PipelineActivity isDrawerOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </DashboardLayout>
  );
};

export default BoardSectionList;
